import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Info, ListChecks } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { StatusDot } from '@/components/ui/StatusDot'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useAuthority } from '@/hooks/useAuthority'
import {
  CredentialingCommissionService,
  type CredentialingCommissionStructure,
  type CredentialingCommissionRule,
} from '@/services/modules/CredentialingService'
import { toast } from '@/components/ui/Toast'

export function CredentialingCommissionStructuresPage() {
  const navigate = useNavigate()
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar tabela' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(CredentialingCommissionService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openDelete, setOpenDelete] = useState<CredentialingCommissionStructure | null>(null)
  const { can } = useAuthority()
  const canCreate = can('credentialing.commission_structures.create')
  const canEdit = can('credentialing.commission_structures.edit')
  const canDelete = can('credentialing.commission_structures.delete')

  const exp = useDataTableExport({
    filename: 'tabelas-comissao-credenciamento',
    service: CredentialingCommissionService,
    filters: sf.filters,
    columns: [
      { header: 'Nome',                accessor: (s: CredentialingCommissionStructure) => s.name },
      { header: 'Meta contratos/mês',  accessor: (s: CredentialingCommissionStructure) => s.monthlyContractSalesGoal ?? 'Sem meta' },
      { header: 'Regras',              accessor: (s: CredentialingCommissionStructure) => s.rules?.length ?? 0 },
      { header: 'Status',              accessor: (s: CredentialingCommissionStructure) => s.status },
    ],
  })

  async function handleDelete() {
    if (!openDelete) return
    try { await CredentialingCommissionService.destroy(openDelete.id); toast.success({ title: 'Removida', description: 'Tabela de comissão removida.' }); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error({ title: 'Erro', description: 'Não foi possível remover.' }) }
  }

  const columns: DataTableColumn<CredentialingCommissionStructure>[] = [
    {
      key: 'name', header: 'Nome', sortable: true, alwaysVisible: true,
      render: s => <span className="font-medium text-gray-800">{s.name}</span>,
    },
    {
      key: 'config', header: 'Configuração', align: 'center', className: 'w-40',
      render: s => (
        <InfoPopover
          ariaLabel="Ver configuração"
          title="Configuração da tabela"
          align="center"
          trigger={
            <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-600 hover:text-gray-900 hover:underline">
              <Info size={14} className="text-blue-500" />
              Detalhes
            </span>
          }
        >
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
            <dt className="text-gray-500">Meta contratos/mês:</dt>
            <dd className="text-gray-800 font-medium">
              {s.monthlyContractSalesGoal != null && s.monthlyContractSalesGoal > 0
                ? s.monthlyContractSalesGoal
                : <span className="text-gray-400 italic">Sem meta</span>}
            </dd>
          </dl>
        </InfoPopover>
      ),
    },
    {
      key: 'rules', header: 'Regras', align: 'center', className: 'w-28',
      render: s => <RulesPopover rules={s.rules ?? []} />,
    },
    {
      key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: s => (
        <StatusDot value={s.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Tabelas de comissão (credenciamento)"
        subtitle="Regras por contrato fechado"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Credenciamento' }, { label: 'Comissionamento' }]}
        actions={canCreate ? (
          <Button onClick={() => navigate('/settings/credentialing/commission-structures/novo')} iconLeft={<Plus size={14} />}>
            Nova tabela
          </Button>
        ) : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.credentialing.commissionstructures"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(s) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />, onClick: () => navigate(`/settings/credentialing/commission-structures/${s.id}/editar`) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(s) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover tabela de comissão?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"? As regras associadas também serão removidas. Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function RulesPopover({ rules }: { rules: CredentialingCommissionRule[] }) {
  const count = rules.length

  return (
    <InfoPopover
      ariaLabel="Ver regras"
      title={`${count} ${count === 1 ? 'regra cadastrada' : 'regras cadastradas'}`}
      align="center"
      minWidth={300}
      maxWidth={420}
      trigger={
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 px-2.5 py-0.5 text-[12px] font-medium hover:bg-blue-100">
          <ListChecks size={12} />
          {count}
        </span>
      }
    >
      {count === 0 ? (
        <p className="text-gray-400 italic text-center py-2">Nenhuma regra cadastrada.</p>
      ) : (
        <ul className="divide-y divide-gray-100 -mx-3 -my-2">
          {[...rules]
            .sort((a, b) => a.contractFrom - b.contractFrom)
            .map(r => (
              <li key={r.id} className="px-3 py-2 flex items-center justify-between gap-3">
                <span className="text-gray-700">
                  {r.contractAction === 'A PARTIR DE'
                    ? `A partir do ${r.contractFrom}º contrato`
                    : `${r.contractFrom}º – ${r.contractTo}º contrato`}
                </span>
                <span className="font-medium text-gray-900 shrink-0">
                  R$ {r.value.toFixed(2).replace('.', ',')}
                </span>
              </li>
            ))}
        </ul>
      )}
    </InfoPopover>
  )
}
